import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useHotelAdminStore } from '../store'
import { apiClient } from '../services/apiClient'
import { extractErrorMessage } from '../utils/errorHandler'

export default function AdminRegisterProtectedPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setAdmin = useHotelAdminStore((state) => state.setAdmin)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  
  const invitationCode = searchParams.get('code')
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    full_name: '',
    hotel_name: '',
    phone: '',
  })

  useEffect(() => {
    // Validate code parameter
    if (!invitationCode) {
      setError('Код приглашения не найден. Пожалуйста, используйте ссылку из письма.')
      setTimeout(() => navigate('/admin-login'), 3000)
    }
  }, [invitationCode, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateForm = (): boolean => {
    if (!formData.email || !formData.password || !formData.full_name || !formData.hotel_name) {
      setError('Пожалуйста, заполните все обязательные поля')
      return false
    }

    if (formData.password.length < 8) {
      setError('Пароль должен быть минимум 8 символов')
      return false
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Пароли не совпадают')
      return false
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Пожалуйста, введите корректный email')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setError('')
      setIsLoading(true)
      
      const response = await apiClient.registerWithCode(
        invitationCode!,
        formData.email,
        formData.password,
        formData.full_name,
        formData.hotel_name,
        formData.phone || undefined
      )

      console.log('Registration response:', response)

      if (response.status === 'ok') {
        setSuccess(true)
        
        // Store admin data and token
        setAdmin({
          id: response.admin_id,
          email: response.email,
          full_name: response.full_name,
          hotel_name: response.hotel_name,
          access_token: response.access_token,
          is_active: true,
          verified: false,
          created_at: new Date().toISOString(),
        })

        // 🔑 Токен автоматически сохраняется в localStorage через setAdmin()
        // Дополнительное сохранение больше не нужно

        // Redirect to admin login page with code after 2 seconds
        setTimeout(() => {
          navigate(`/admin-login?code=${invitationCode}&new=true`)
        }, 2000)
      } else {
        setError(response.detail || 'Ошибка регистрации')
      }
    } catch (err: any) {
      console.error('Registration error:', err)
      setError(extractErrorMessage(err) || 'Ошибка при регистрации')
    } finally {
      setIsLoading(false)
    }
  }

  if (!invitationCode) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-600 to-blue-600 p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">❌ Ошибка</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">Перенаправление...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-600 to-blue-600 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">🏨 HotelsProd</h1>
            <h2 className="text-xl font-semibold text-gray-600 mt-2">Регистрация администратора</h2>
            <p className="text-sm text-gray-500 mt-2">Защищённая регистрация по приглашению</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              ⚠️ {error}
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              ✅ Регистрация успешна! Проверьте вашу почту для верификации.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email администратора
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                ФИО администратора
              </label>
              <input
                id="full_name"
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Иван Иванов"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label htmlFor="hotel_name" className="block text-sm font-medium text-gray-700 mb-1">
                Название отеля
              </label>
              <input
                id="hotel_name"
                type="text"
                name="hotel_name"
                value={formData.hotel_name}
                onChange={handleChange}
                placeholder="Grand Hotel Moscow"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Телефон (опционально)
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+7 (999) 999-99-99"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Пароль (минимум 8 символов)
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                Подтверждение пароля
              </label>
              <input
                id="passwordConfirm"
                type="password"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-semibold py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Уже зарегистрированы?{' '}
              <a href="/admin-login" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                Войти
              </a>
            </p>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-white/80">
          <p>Защищённая регистрация по приглашению</p>
        </div>
      </div>
    </div>
  )
}
